import React, { useContext } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import CustomerServicePage from './CustomerServicePage';
import SuccessPage from './SuccessPage';
import type { CartItem } from '../App';
import { CartContext } from '../App';
import EmptyCartPremium from './EmptyCartPremium';
import { useLanguage } from './Header';
import { getTranslation, type Language } from '../translations';

// Import from cart module
import {
  CartItemCard,
  CartSidebar,
  CartActions,
  CartDeleteModal,
  CartContactModal,
  useCartPage,
  getConditionLabel,
  getBatteryLabel,
  formatProductName
} from './cart';


// Add global styles for modal animation
const GlobalModalStyles = createGlobalStyle`
  @keyframes modalAppear {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes contactOverlayIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes contactOverlayOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes contactDialogIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes contactDialogOut {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(0.95); }
  }
  @keyframes contactSheetIn {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
  @keyframes contactSheetOut {
    from { transform: translateY(0); }
    to { transform: translateY(100%); }
  }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem;
  padding-top: clamp(7rem, 8vw, 8.5rem);
  background: transparent;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 1.5rem;
    padding-top: 6.5rem;
    padding-bottom: 3rem;
  }

  @media (max-width: 640px) {
    padding: 1.5rem;
    padding-top: 6rem;
    padding-bottom: 3rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    padding-top: 5.5rem;
    padding-bottom: 2.5rem;
  }
`;

const BreadcrumbNav = styled.nav`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1.5rem;
  color: #666;
  font-size: 0.9rem;
  
  .dark & {
    color: #94a3b8;
  }
`;

const BreadcrumbBackButton = styled.button`
  background: none;
  border: none;
  color: #475569; /* neutral slate */
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: #111827;
    transform: translateX(-2px);
  }
  
  &::before {
    content: '←';
    font-size: 1.2rem;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #666;
  
  .dark & {
    color: #94a3b8;
  }
`;

const BreadcrumbLink = styled.a`
  color: #475569;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: #111827;
    text-decoration: underline;
  }
  
  .dark & {
    color: #94a3b8;
  }
  .dark &:hover {
    color: #e5e7eb;
  }
`;

const CartHeader = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.5rem;
    border-radius: 16px;
  }

  @media (max-width: 640px) {
    padding: 1rem 1.125rem;
    margin-bottom: 1.25rem;
    border-radius: 12px;
    gap: 0.75rem;
  }

  .dark & {
    background: #0f1216;
    border-color: #2e3441;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.55);
  }
`;

const BackArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #0f172a;
    transform: translateX(-2px);
  }

  .dark & {
    background: #0f1216;
    border-color: #334155;
    color: #94a3b8;

    &:hover {
      background: #1e293b;
      border-color: #475569;
      color: #e2e8f0;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const HeaderContent = styled.div`
  text-align: left;
`;

const CartTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }

  @media (max-width: 360px) {
    font-size: 1.35rem;
  }

  .dark & {
    color: #f1f5f9;
  }
`;

const CartSubtitle = styled.p`
  font-size: 1rem;
  color: #6e6e73;
  margin: 0.25rem 0 0;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin: 0.2rem 0 0;
  }
  
  @media (max-width: 360px) {
    font-size: 0.85rem;
  }
  
  .dark & {
    color: #94a3b8;
  }
`;

const CartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 2.5rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1.25rem;
  }
  
  @media (max-width: 360px) {
    gap: 1rem;
  }
`;

const CartItems = styled.div`
  background: transparent;
  border-radius: 16px;
  padding: 0;
  position: relative;
  z-index: 10;
  
  @media (max-width: 480px) {
    border-radius: 12px;
    padding: 0;
  }
`;

// RightSidebar is defined inside CartSidebar component

const CartItemContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1.5rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border-radius: 16px;
  transition: all 0.3s ease;
  position: relative;
  background-color: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    gap: 1.25rem;
    padding: 1.25rem;
    border-radius: 14px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      "image info"
      "image actions";
    gap: 0.875rem;
    padding: 1rem 1.125rem;
    border-radius: 12px;
    margin-bottom: 0.875rem;
  }

  @media (max-width: 360px) {
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    border-radius: 10px;
    margin-bottom: 0.75rem;
  }
  
  .dark & {
    background-color: #0f172a;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.6);
    border: 1px solid #1f2937;
  }
`;

const DeviceImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: all 0.3s ease;
`;

const DeviceInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 1.5rem;

  @media (max-width: 640px) {
    grid-area: info;
    padding: 0;
  }
`;

const DeviceName = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 360px) {
    font-size: 1.1rem;
    margin-bottom: 0.875rem;
  }
  
  .dark & {
    color: #e5e7eb;
  }
`;

const DevicePrice = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: #111827;
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 360px) {
    font-size: 1.1rem;
  }
  
  .dark & {
    color: #e5e7eb;
  }
`;

const DeviceSpecs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const SpecRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  
  &:not(:last-child) {
    margin-bottom: 0.15rem;
  }
`;

const SpecLabel = styled.span`
  color: #666;
  font-weight: 500;
  
  .dark & {
    color: #94a3b8;
  }
`;

const SpecValue = styled.span`
  color: #333;
  
  .dark & {
    color: #e5e7eb;
  }
`;

// New styled components for improved UI
const EarningsCard = styled.div`
  padding: 12px 16px;
  margin-top: 15px;
  margin-bottom: 10px;
  background-color: #f7f8fa;
  border-radius: 12px;
  border: 1px solid #e6edf5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  @media (max-width: 480px) {
    padding: 10px 14px;
    margin-top: 12px;
    margin-bottom: 8px;
    border-radius: 10px;
  }
  
  @media (max-width: 360px) {
    padding: 8px 12px;
    margin-top: 10px;
    margin-bottom: 6px;
    border-radius: 8px;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  
  .dark & {
    background-color: rgba(30, 41, 59, 0.7);
    border-color: #334155;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }
`;

const EarningsLabel = styled.span`
  font-weight: 600;
  color: #475569;
  font-size: 0.95rem;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 360px) {
    font-size: 0.85rem;
  }
  
  .dark & {
    color: #60a5fa;
  }
`;

const EarningsValue = styled.span`
  font-weight: 700;
  font-size: 1.2rem;
  color: #111827;
  background: #ffffff;
  padding: 5px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    padding: 4px 10px;
    border-radius: 6px;
  }
  
  @media (max-width: 360px) {
    font-size: 1rem;
    padding: 3px 8px;
    border-radius: 6px;
  }
  
  .dark & {
    color: #93c5fd;
    background: rgba(255, 255, 255, 0.06);
    box-shadow: none;
  }
`;

// Styled componenten voor de batterij-indicator
const BatteryContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  flex-grow: 1;
`;

const BatteryIconVisual = styled.div`
  width: 28px;
  height: 14px;
  border: 1.5px solid #a0a0a0;
  border-radius: 4px;
  position: relative;
  display: flex;
  align-items: center;
  padding: 1.5px;
  box-sizing: border-box;

  &::after {
    content: '';
    position: absolute;
    right: -3px;
    top: 50%;
    transform: translateY(-50%);
    width: 1.5px;
    height: 6px;
    background-color: #a0a0a0;
    border-top-right-radius: 1px;
    border-bottom-right-radius: 1px;
  }
`;

const BatteryLevel = styled.div<{ percentage: number; color: string }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => props.color};
  border-radius: 2px;
  transition: width 0.4s ease, background 0.4s ease;
`;

const BatteryLabelText = styled.span`
    color: #333;
    font-weight: 500;
    font-size: 0.95rem;
    
    .dark & {
      color: #e5e7eb;
    }
`;

const BatteryKnob = styled.div`
  position: absolute;
  right: -4px;
  top: 50%;
  transform: translateY(-50%);
  width: 2px;
  height: 8px;
  background-color: #ddd;
  border-top-right-radius: 2px;
  border-bottom-right-radius: 2px;
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  
  @media (max-width: 480px) {
    grid-area: actions;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
  }
`;

const ItemPrice = styled.div`
  color: #475569;
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.8rem;
  
  .dark & {
    color: #60a5fa;
  }
`;

const RemoveSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

// Removed - using newer RemoveButton below

const Summary = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 5.5rem;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
  
  @media (max-width: 1024px) {
    position: relative;
    top: 0;
  }
  
  @media (max-width: 768px) {
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    border-radius: 12px;
    gap: 14px;
  }
  
  @media (max-width: 360px) {
    border-radius: 10px;
    gap: 12px;
  }
  
  .dark & {
    background: #0f172a;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.6);
    border: 1px solid #1f2937;
  }
`;

const SummaryTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0;
  padding: 1.5rem;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    padding: 1rem;
  }
  
  @media (max-width: 360px) {
    font-size: 1.1rem;
    padding: 0.875rem;
  }
  
  .dark & {
    color: #e5e7eb;
  }
`;

const SummaryTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  td, th {
    padding: 1rem 1.5rem;
    text-align: left;
  }
  
  @media (max-width: 768px) {
    td, th {
      padding: 0.875rem 1.25rem;
    }
  }
  
  @media (max-width: 480px) {
    td, th {
      padding: 0.75rem 1rem;
    }
  }
  
  @media (max-width: 360px) {
    td, th {
      padding: 0.625rem 0.875rem;
    }
  }
  
  tr:not(:last-child) td {
    border-bottom: 1px solid #f4f4f4;
  }
  
  .dark & tr:not(:last-child) td {
    border-bottom: 1px solid #1f2937;
  }
`;

const TableHeader = styled.th`
  font-weight: 600;
  color: #666;
  font-size: 0.9rem;
  padding: 1rem 1.5rem;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.75rem 1rem;
  }
  
  @media (max-width: 360px) {
    font-size: 0.8rem;
    padding: 0.625rem 0.875rem;
  }
  
  .dark & {
    color: #94a3b8;
  }
`;

const TableCell = styled.td`
  vertical-align: top;
  padding: 1rem 1.5rem;
  
  &.device-cell {
    max-width: 200px;
  }
  
  &.price-cell {
    text-align: right;
    white-space: nowrap;
  }
  
  .dark & {
    color: #e5e7eb;
  }
`;

const DeviceDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const DeviceModel = styled.div`
  font-weight: 500;
  margin-bottom: 0.3rem;
`;

const DeviceSpec = styled.div`
  font-size: 0.85rem;
  color: #666;
  
  .dark & {
    color: #94a3b8;
  }
`;

const SubtotalRow = styled.tr`
  font-weight: 500;
`;

const TotalSection = styled.div`
  background-color: #f3f4f6;
  padding: 1.5rem;
  margin: 0;
  border-top: 1px solid #e5e7eb;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
  
  @media (max-width: 360px) {
    padding: 0.875rem;
  }
  
  .dark & {
    background-color: rgba(17, 19, 23, 0.8);
    border-top-color: #2e3441;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.3rem;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 360px) {
    font-size: 1.1rem;
  }
`;

const TotalAmount = styled.span`
  color: #111827;
  font-size: 1.4rem;
  font-weight: 700;
  background: #ffffff;
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    padding: 6px 14px;
    border-radius: 6px;
  }
  
  @media (max-width: 360px) {
    font-size: 1.2rem;
    padding: 5px 12px;
    border-radius: 6px;
  }
  
  .dark & {
    color: #e5e7eb;
    background: rgba(255, 255, 255, 0.04);
    box-shadow: none;
  }
`;

const PriceText = styled.span`
  color: #111827;
  font-weight: 500;
  font-size: 1rem;
  
  .dark & {
    color: #e5e7eb;
  }
`;

const CheckoutButton = styled.button`
  background: #374151;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1.2rem;
  width: 100%;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  
  @media (max-width: 768px) {
    padding: 1.1rem;
    font-size: 1rem;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
    border-radius: 10px;
  }
  
  @media (max-width: 360px) {
    padding: 0.9rem;
    font-size: 0.9rem;
    border-radius: 8px;
  }
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);

  &:hover {
    background: #4b5563;
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.35);
  }

  &:active {
    transform: translateY(0);
    background: #374151;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  .dark & {
    background: #374151;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  }
  .dark &:hover {
    background: #4b5563;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.45);
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
  
  .dark & {
    color: #94a3b8;
  }
`;

const EmptyCartMessage = styled.p`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  
  .dark & {
    color: #e5e7eb;
  }
`;

const CartQuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CartQuantityButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: white;
  color: #0099ff;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background: #f5f5f7;
    border-color: #4b5563;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    color: #ccc;
    border-color: #eee;
    background: #f8f8f8;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CartQuantityDisplay = styled.span`
  width: 40px;
  text-align: center;
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
`;

const AddDeviceButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  background-color: #f0f9ff;
  border: 1px solid #e0f2fe;
  color: #0ea5e9;
  padding: 0.8rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e0f2fe;
  }
`;

// Removed - using newer AddDeviceButton below

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" fill="#4b5563" stroke="white" />
    <line x1="12" y1="8" x2="12" y2="16" stroke="white" />
    <line x1="8" y1="12" x2="16" y2="12" stroke="white" />
  </svg>
);

// Add a new TrashIcon component for the remove button
const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

interface CartPageProps {
  items: CartItem[];
  onRemoveItem: (id: string) => void;
  onBack: () => void;
  onSuccessNavigate?: (orderNumber: string, email?: string, appointmentDetails?: any) => void;
}

// Helper functions (getTranslatedValue, getSpecLabel, formatPrice, formatProductName,
// getConditionLabel, getBatteryLabel) are now imported from './cart'

// Re-export for backward compatibility with other files that import from CartPage
export { getTranslatedValue, getSpecLabel, formatPrice, formatProductName } from './cart';

/* Old helper functions removed - now imported from './cart' */

// Component voor het tonen van gekozen opties zoals in de afbeelding
const ProductOptionsList = styled.div`
  margin: 1.5rem 0;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  
  .dark & {
    background: #0f172a;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.6);
    border: 1px solid #1f2937;
  }
`;

const ProductOptionsHeader = styled.div`
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: #555;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  background-color: #f5f5f7;
  
  .dark & {
    color: #cbd5e1;
    background-color: #111827;
  }
`;

const OptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
  
  .dark & {
    border-bottom: 1px dashed #334155;
  }
`;

const OptionQuestion = styled.div`
  color: #666;
  font-weight: 500;
  font-size: 0.95rem;
  
  .dark & {
    color: #94a3b8;
  }
`;

const OptionAnswer = styled.div`
  color: #333;
  font-weight: 600;
  font-size: 0.95rem;
  text-align: right;
  
  .dark & {
    color: #e5e7eb;
  }
`;

const PriceSection = styled.div`
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.6);
  }
`;

const SummaryPriceLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #555;
  
  .dark & {
    color: #cbd5e1;
  }
`;

const PriceValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #4CAF50;
  display: flex;
  align-items: baseline;
  
  & > span.currency {
    font-size: 1.8rem;
    margin-right: 0.3rem;
  }
  
  .dark & {
    color: #86efac; /* green-300 */
  }
`;

const QuantitySection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  
  .dark & {
    background: #0f172a;
    border: 1px solid #1f2937;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.6);
  }
`;

// Volledig nieuwe, professionele cart layout
const ModernCartContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0;
  background: transparent;
  
  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

// Update CartCard to be used for the top box with product info only
const CartCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.06);
  border: 1px solid #e6edf5;
  margin-bottom: 2rem;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  
  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 20px;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.1rem 1rem;
    border-radius: 16px;
    margin-bottom: 1.25rem;
  }
`;

// Create a separate component for the buttons box
const ButtonsCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.06);
  border: 1px solid #e6edf5;
  padding: 1.5rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    border-radius: 20px;
    padding: 1.25rem;
    margin-top: 1.25rem;
  }
  
  @media (max-width: 480px) {
    border-radius: 16px;
    padding: 1rem;
    margin-top: 1rem;
  }
  
  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
  }
`;

// Update ButtonsSection to be used inside the ButtonsCard
const ButtonsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  
  @media (max-width: 480px) {
    gap: 0.6rem;
  }
`;

const ProductHeader = styled.div`
  display: flex;
  gap: 1.25rem;
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  position: relative;
  align-items: flex-start;
  border-bottom: 1px solid #e5e7eb;

  .dark & {
    border-bottom-color: #1e293b;
  }

  @media (max-width: 768px) {
    gap: 1rem;
    padding-bottom: 1.25rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    gap: 0.875rem;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.375rem;
`;

const ImageSection = styled.div`
  width: 100px;
  height: 120px;
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  flex-shrink: 0;

  .dark & {
    background: rgba(255, 255, 255, 0.04);
    border-color: #1e293b;
  }

  @media (max-width: 768px) {
    width: 88px;
    height: 105px;
    border-radius: 14px;
  }

  @media (max-width: 480px) {
    width: 76px;
    height: 92px;
    border-radius: 12px;
    padding: 0.4rem;
  }
`;

const ProductPrice = styled.div`
  flex: 1;
`;

const PriceTitle = styled.span`
  font-size: 0.75rem;
  color: #94a3b8;
  font-weight: 500;
  display: block;
  margin-bottom: 0.125rem;

  .dark & {
    color: #64748b;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const PriceAmount = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.15;
  letter-spacing: -0.02em;

  .dark & {
    color: #f1f5f9;
  }

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 480px) {
    gap: 0.35rem;
  }
`;

const QuantityLabel = styled.span`
  font-size: 0.875rem;
  color: #6e6e73;
  margin-bottom: 0.5rem;
  font-weight: 500;
  
  .dark & {
    color: #94a3b8;
  }
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 0.35rem;
  }
`;

const QuantityInputWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  width: fit-content;
  background-color: #f9fafb;
  
  .dark & {
    border-color: #334155;
    background-color: rgba(255, 255, 255, 0.06);
  }
  
  @media (max-width: 480px) {
    border-radius: 8px;
  }
`;

const QuantityButton = styled.button`
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #007aff;
  font-size: 1.25rem;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f0f0f0;
  }

  &:disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }
  
  .dark & {
    color: #93c5fd;
  }
  .dark &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 1.1rem;
  }
  
  @media (max-width: 360px) {
    width: 30px;
    height: 30px;
    font-size: 1rem;
  }
`;

const QuantityValue = styled.span`
  padding: 0 0.75rem;
  font-weight: 600;
  color: #1d1d1f;
  font-size: 1rem;
  min-width: 20px;
  text-align: center;
  
  .dark & {
    color: #e5e7eb;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0 0.65rem;
    min-width: 18px;
  }
`;

const OptionsSection = styled.div`
  padding: 0;

  @media (max-width: 480px) {
    padding: 0;
  }
`;

const OptionsTitle = styled.span`
  font-size: 0.95rem;
  display: block;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.75rem;

  .dark & { color: #e2e8f0; }

  @media (max-width: 480px) {
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailOptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 0;
  border-bottom: 1px dashed #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0;
  }

  .dark & {
    border-bottom-color: #1e293b;
  }
`;

const OptionLabel = styled.span`
  font-size: 0.9rem;
  color: #1e293b;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;

  svg {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    color: #334155;
    padding: 6px;
    background: #f1f5f9;
    border-radius: 10px;
    box-sizing: border-box;
  }

  .dark & { color: #e5e7eb; }
  .dark & svg { color: #cbd5e1; background: #1a1f2e; }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    gap: 0.625rem;
    svg { width: 28px; height: 28px; padding: 5px; border-radius: 8px; }
  }
`;

const OptionValue = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  background: #f1f5f9;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;

  .dark & { color: #cbd5e1; background: #1a1f2e; }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.3rem 0.6rem;
  }
`;

// Confirmation Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  will-change: opacity;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: scale(1);
  animation: modalAppear 0.2s ease-out;
  
  @keyframes modalAppear {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .dark & {
    background: #0f172a;
    border: 1px solid #1f2937;
    color: #e5e7eb;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const ModalIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #fee2e2;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: #dc2626;
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const ModalMessage = styled.p`
  color: #6b7280;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;
  
  ${props => props.variant === 'primary' ? `
    background: #dc2626;
    color: white;
    border-color: #dc2626;
    
    &:hover {
      background: #b91c1c;
      border-color: #b91c1c;
    }
  ` : `
    background: white;
    color: #374151;
    border-color: #d1d5db;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
`;


// Warning Icon Component
const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
  </svg>
);

const InfoText = styled.p`
  margin: 1.5rem 0;
  font-size: 16px;
  color: #667085;
  line-height: 1.5;
`;

const PrimaryButton = styled.button`
  width: 100%;
  background: #374151;
  color: white;
  border: none;
  border-radius: 14px;
  padding: 1.25rem;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
  
  @media (max-width: 768px) {
    padding: 1.1rem;
    font-size: 1rem;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 0.95rem;
    font-size: 0.95rem;
    border-radius: 12px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.22);
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: #4b5563;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.35);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  background: transparent;
  color: #111827;
  border: 1.5px solid #cbd5e1;
  border-radius: 14px;
  padding: calc(1.25rem - 1.5px);
  font-size: 1.05rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 0.75rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #f3f4f6;
    color: #0f172a;
  }

  &:active:not(:disabled) {
    background-color: #e5e7eb;
    border-color: #cbd5e1;
  }

  &:disabled {
    color: #9ca3af;
    border-color: #e5e7eb;
    background: transparent;
  }
  
  .dark & {
    color: #e5e7eb;
    border-color: #2e3441;
  }
  .dark &:hover:not(:disabled) {
    background-color: rgba(148, 163, 184, 0.06);
    border-color: #3a4250;
    color: #e5e7eb;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 1rem;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 0.9rem;
    font-size: 0.95rem;
    border-radius: 12px;
    border-width: 1.25px;
    margin-top: 0.6rem;
  }
`;

const QuoteButtonContainer = styled.div`
  margin-top: 1.5rem;
  text-align: center;
`;

const QuoteButton = styled.button`
  background: transparent;
  border: none;
  color: #6e6e73;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: not-allowed;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.7;
`;

const OpacityLayer = styled.div`
  opacity: 0.7;
`;

const ComingSoonBadge = styled.span`
  background-color: #2c2c2e;
  color: #e5e5ea;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.25rem 0.6rem;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 480px) {
    font-size: 0.65rem;
    padding: 0.22rem 0.5rem;
    border-radius: 7px;
  }
`;

const EstimationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  margin: 0.75rem 0 1.25rem 0;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f7 100%);
  border-radius: 100px;
  border: 1px solid #e5e5e7;

  .dark & {
    background: linear-gradient(135deg, #1a1f2e 0%, #151922 100%);
    border-color: #2e3441;
  }

  @media (max-width: 480px) {
    padding: 7px 12px;
    gap: 6px;
    margin: 0.5rem 0 1rem 0;
  }
`;

const EstimationBadgeIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8e8e93;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }

  .dark & {
    color: #86868b;
  }

  @media (max-width: 480px) {
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const EstimationBadgeText = styled.span`
  font-weight: 500;
  font-size: 0.8rem;
  color: #6e6e73;
  line-height: 1.3;

  .dark & {
    color: #a1a1a6;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;

  .info-icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: #0f172a;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 16px;
      height: 16px;
      color: white;
    }
  }

  span {
    font-size: 0.85rem;
    font-weight: 500;
    color: #475569;
  }

  .dark & {
    background: #1a1f2e;
    border-color: #2d3548;

    .info-icon {
      background: #e2e8f0;
      svg { color: #0f172a; }
    }
    span { color: #94a3b8; }
  }

  @media (max-width: 480px) {
    padding: 0.625rem 0.875rem;
    gap: 0.625rem;
    .info-icon { width: 28px; height: 28px; border-radius: 8px; svg { width: 14px; height: 14px; } }
    span { font-size: 0.8rem; }
  }
`;

const CartTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  margin-top: 1rem;

  .dark & {
    background: linear-gradient(135deg, #1a1f2e 0%, #151922 100%);
    border-color: #2e3441;
  }

  @media (max-width: 480px) {
    padding: 1rem 1.25rem;
    border-radius: 12px;
  }
`;

const CartTotalLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  .dark & { color: #94a3b8; }
`;

const CartTotalPrice = styled.div`
  text-align: right;
`;

const CartTotalAmount = styled.span`
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
  display: block;
  letter-spacing: -0.02em;
  line-height: 1.1;

  .dark & { color: #f1f5f9; }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const CartTotalVat = styled.span`
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
  display: block;
  margin-top: 0.2rem;

  .dark & { color: #94a3b8; }
`;

const UnknownStateToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  flex-grow: 1;

  .switch {
    width: 34px;
    height: 20px;
    background-color: #d1d5db;
    border-radius: 10px;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 16px;
      height: 16px;
      background-color: white;
      border-radius: 50%;
      box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    }
  }

  .label {
    color: #6b7280;
    font-weight: 500;
    font-size: 0.95rem;
  }
`;

// Add a chevron right icon component
const ChevronRightIcon = () => (
  <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m9 5 7 7-7 7"></path>
  </svg>
);

// Create a new RemoveButton component
const ItemRemoveButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
  padding: 0;
  cursor: pointer;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  will-change: opacity;
  z-index: 10;

  &:hover {
    background: #ef4444;
    color: white;
    border-color: #ef4444;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 480px) {
    top: 1rem;
    right: 1rem;
    width: 28px;
    height: 28px;
    svg { width: 14px; height: 14px; }
  }
`;

// Improve the batch selection bar styling
const BatchSelectionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  background-color: #f0f9ff;
  border-radius: 12px;
  border: 1px solid #bae6fd;
`;

const BatchSelectionText = styled.span`
  color: #0369a1;
  font-size: 0.95rem;
  font-weight: 500;
`;

const BatchSelectionButton = styled.button`
  background-color: #0284c7;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: #0369a1;
  }
`;

// Add a selection icon component
const SelectionIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 3H5C3.89543 3 3 3.89543 3 5V9C3 10.1046 3.89543 11 5 11H9C10.1046 11 11 10.1046 11 9V5C11 3.89543 10.1046 3 9 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 13H5C3.89543 13 3 13.8954 3 15V19C3 20.1046 3.89543 21 5 21H9C10.1046 21 11 20.1046 11 19V15C11 13.8954 10.1046 13 9 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 3H15C13.8954 3 13 3.89543 13 5V9C13 10.1046 13.8954 11 15 11H19C20.1046 11 21 10.1046 21 9V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 13H15C13.8954 13 13 13.8954 13 15V19C13 20.1046 13.8954 21 15 21H19C20.1046 21 21 20.1046 21 19V15C21 13.8954 20.1046 13 19 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DeviceNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
  border-radius: 12px;
`;

const NavButton = styled.button`
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  svg {
    width: 18px;
    height: 18px;
    stroke: #495057;
  }

  &:hover {
    background-color: #f1f3f5;
    border-color: #ced4da;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NavText = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
`;

// Batch selection components are defined above

// Create a quantity control component
const ProductQuantityControl = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1.5rem;
`;

const QuantityControlButtons = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  width: fit-content;
`;

const ProductQuantityButton = styled.button`
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4169e1;
  font-size: 1.25rem;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
  }

  &:disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }
`;

const ProductQuantityValue = styled.span`
  padding: 0 1rem;
  font-weight: 600;
  color: #111827;
  font-size: 1rem;
`;

// Completely redesign the navigation buttons
const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #4169e1; // Match the blue in the UI
  border: none;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  box-shadow: 0 4px 8px rgba(65, 105, 225, 0.25);
  z-index: 20;
  transition: all 0.2s ease;

  &:hover {
    background: #3451b2;
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0 6px 12px rgba(65, 105, 225, 0.3);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &:disabled {
    background: #E5E7EB;
    cursor: not-allowed;
    box-shadow: none;
    color: #9CA3AF;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const PrevButton = styled(NavigationButton)`
  left: -30px;

  @media (max-width: 768px) {
    left: 4px;
  }
`;

const NextButton = styled(NavigationButton)`
  right: -30px;
  
  @media (max-width: 768px) {
    right: 4px;
  }
`;

// Enhance the device counter
const DeviceCounter = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: #4169e1;
  color: white;
  border-radius: 24px;
  padding: 6px 14px;
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  box-shadow: 0 2px 4px rgba(65, 105, 225, 0.2);
  z-index: 10;
`;

// Update the trash icon to match the UI style
const TrashIconButton = styled.div`
  position: absolute;
  top: 12px;
  right: 80px;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  will-change: opacity;
  z-index: 10;

  &:hover {
    background: rgba(239, 68, 68, 0.15);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2;
  }
`;

// Create simpler arrow icons that match the UI style
const LeftArrowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RightArrowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Also add labels to make it clear what the navigation buttons do
const ButtonLabel = styled.span`
  position: absolute;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
`;

const PrevButtonWithLabel = styled.div`
  position: relative;
  
  &:hover ${ButtonLabel} {
    opacity: 1;
  }
`;

const NextButtonWithLabel = styled.div`
  position: relative;
  
  &:hover ${ButtonLabel} {
    opacity: 1;
  }
`;

// Add these new styled components for the service benefits section
const ServiceSection = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.06);
  border: 1px solid #e6edf5;

  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
  }

  @media (max-width: 768px) {
    border-radius: 20px;
    padding: 20px;
  }

  @media (max-width: 480px) {
    border-radius: 16px;
    padding: 16px;
  }

  @media (max-width: 360px) {
    border-radius: 14px;
    padding: 14px;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 0 20px 0;
  color: #111827;

  .dark & {
    color: #e5e7eb;
  }

  @media (max-width: 768px) {
    font-size: 19px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 14px;
  }

  @media (max-width: 360px) {
    font-size: 17px;
    margin-bottom: 12px;
  }
`;

const ServiceFeature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 16px;
  }
  
  @media (max-width: 360px) {
    gap: 10px;
    margin-bottom: 14px;
  }
`;

const ServiceIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #475569; /* icon stroke via currentColor */
  background: linear-gradient(180deg, #f6f8fb 0%, #eef2f7 100%);
  border: 1px solid #e5eaf2;
  border-radius: 12px;
  
  .dark & {
    color: #94a3b8; /* slate-400 */
    background: linear-gradient(180deg, #161a21 0%, #0f1216 100%);
    border: 1px solid #2e3441;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    border-radius: 10px;
  }

  @media (max-width: 360px) {
    width: 30px;
    height: 30px;
  }
`;

const ServiceText = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #333;
  line-height: 1.5;

  .dark & {
    color: #cbd5e1;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.45;
  }

  @media (max-width: 360px) {
    font-size: 0.92rem;
  }
`;

const FeatureTitle = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: #111827;
  letter-spacing: -0.01em;
  margin-bottom: 4px;
  
  .dark & {
    color: #e5e7eb;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 3px;
  }

  @media (max-width: 360px) {
    font-size: 0.92rem;
  }
`;

const ServiceDivider = styled.hr`
  border: none;
  border-top: 1px dashed #e6eaf0;
  margin: 8px 0;
  
  .dark & {
    border-top-color: #2e3441;
  }

  @media (max-width: 480px) {
    margin: 6px 0;
  }
`;

const SupportSection = styled.div`
  background: #f9fafb;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  border-radius: 16px;

  .dark & {
    background: rgba(30, 41, 59, 0.7);
    border: 1px solid #1f2937;
  }
`;

const SupportIcon = styled.div`
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .dark & {
    color: #60a5fa;
  }
`;

const ChatButtonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StartChatButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #374151;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);

  &:hover {
    background: #4b5563;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.35);
  }

  .dark & {
    background: rgba(255, 255, 255, 0.06);
    color: #e5e7eb;
    border: 1px solid #2e3441;
    box-shadow: none;
  }
  .dark &:hover {
    background: rgba(148, 163, 184, 0.08);
    border-color: #3a4250;
  }
`;

const SupportText = styled.p`
  margin: 0;
  font-weight: 600;
  font-size: 1.1rem;
  color: #333;
`;

const SupportLink = styled.a`
  color: #475569;
  text-decoration: none;
  margin-left: 8px;
  
  &:hover {
    text-decoration: underline;
  }
`;

/* Oude Waardeontwikkeling styled components opgeschoond */

// Icons voor trend indicators
/* TrendDownIcon verwijderd */

const DownArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="0.7em" viewBox="0 0 512 512" fill="currentColor">
    <path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"></path>
  </svg>
);

// Add back the icon components that were removed
const ShippingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 3H1V16H16V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 8H20L23 11V16H16V8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="5.5" cy="18.5" r="2.5" stroke="currentColor" strokeWidth="2" />
    <circle cx="18.5" cy="18.5" r="2.5" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const PrivacyIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" />
    <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

const ReturnIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12C3 13.1819 3.23279 14.3522 3.68508 15.4442C4.13738 16.5361 4.80031 17.5282 5.63604 18.364C6.47177 19.1997 7.46392 19.8626 8.55585 20.3149C9.64778 20.7672 10.8181 21 12 21C13.1819 21 14.3522 20.7672 15.4442 20.3149C16.5361 19.8626 17.5282 19.1997 18.364 18.364C19.1997 17.5282 19.8626 16.5361 20.3149 15.4442C20.7672 14.3522 21 13.1819 21 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 8L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 9V3H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrophyIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 21H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 17V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M17 7V4H7V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M17 4H19C19.5304 4 20.0391 4.21071 20.4142 4.58579C20.7893 4.96086 21 5.46957 21 6V8C21 9.5913 20.3679 11.1174 19.2426 12.2426C18.1174 13.3679 16.5913 14 15 14H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M7 4H5C4.46957 4 3.96086 4.21071 3.58579 4.58579C3.21071 4.96086 3 5.46957 3 6V8C3 9.5913 3.63214 11.1174 4.75736 12.2426C5.88258 13.3679 7.4087 14 9 14H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 14C13.1046 14 14 13.1046 14 12V8C14 6.89543 13.1046 6 12 6C10.8954 6 10 6.89543 10 8V12C10 13.1046 10.8954 14 12 14Z" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const ChatIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 12C21 16.9706 16.9706 21 12 21C10.2289 21 8.57736 20.5154 7.17317 19.6699L3 21L4.3301 16.8268C3.48458 15.4226 3 13.7711 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BatteryIconLarge = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="6" width="18" height="12" rx="2" stroke="#4B54CE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 10H22V14H20V10Z" fill="#4B54CE" />
    <path d="M6 10H14V14H6V10Z" fill="#4B54CE" />
  </svg>
);

const CalendarIconLarge = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="#4B54CE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 2V6" stroke="#4B54CE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 2V6" stroke="#4B54CE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 10H21" stroke="#4B54CE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PriceExplanationSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
`;

/* Oude prijs-verklaring UI opgeschoond */

const ArrowDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4L12 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18 14L12 20L6 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChipIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 4V2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 4V2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 22V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 22V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 9H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 15H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 9H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 15H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const BatteryIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="18" height="12" rx="2"></rect><path d="M20 10H22V14H20V10Z" fill="#6b7280"></path>
  </svg>
);

const BoxIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>
);

const SignalIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 16.1A5 5 0 0 1 5.9 13H5a5 5 0 0 1 5-5 5 5 0 0 1 5 5h-.9a5 5 0 0 1 3.9 1.9" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M11 20a9 9 0 0 1-9-9" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 20a5 5 0 0 1-5-5" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ScreenIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke="#6b7280" strokeWidth="2" />
    <line x1="8" y1="21" x2="16" y2="21" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
    <line x1="12" y1="17" x2="12" y2="21" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Helper to get the right icon for each spec
const getSpecIcon = (key: string): React.ReactNode => {
  switch (key) {
    case 'storage':
    case 'ram':
    case 'processor':
      return <ChipIcon />;
    case 'battery':
      return <BatteryIcon />;
    case 'condition':
      return <StarIcon />;
    case 'networkType':
    case 'cellularSupport':
      return <SignalIcon />;
    case 'screenSize':
    case 'watchSize':
      return <ScreenIcon />;
    case 'modelYear':
      return <CalendarIcon />;
    case 'accessories':
    case 'watchBand':
      return <BoxIcon />;
    // iPhone specific
    case 'functionality':
      return <StarIcon />;
    case 'screenCondition':
      return <ScreenIcon />;
    case 'housingCondition':
      return <BoxIcon />;
    case 'batteryCapacity':
      return <BatteryIcon />;
    default:
      return <div style={{ width: '18px', height: '18px' }} />; // Placeholder for null
  }
};

// Live Warning Price Forecast Component - Horizontal Layout
const PriceForcastComponent: React.FC<{ items: CartItem[] }> = ({ items }) => {
  const { language } = useLanguage();
  const { quantities } = useContext(CartContext);
  const t = (key: any) => getTranslation(language as Language, key);

  // Bereken de gemiddelde huidige prijs van alle items in de winkelwagen
  const totalValue = items.reduce((sum, item) => {
    if (item.price === -1) return sum; // Skip "Op Aanvraag" items
    return sum + (item.price * (quantities[item.id] || 1));
  }, 0);

  const totalItems = items.filter(item => item.price !== -1).length;
  const currentPrice = totalValue > 0 ? totalValue : 430;

  // Bereken realistische prijsdaling gebaseerd op apparaattype
  const getDepreciationRate = () => {
    const hasIPhone = items.some(item => item.deviceType.toLowerCase().includes('iphone'));
    const hasSamsung = items.some(item => item.deviceType.toLowerCase().includes('samsung'));
    const hasAppleWatch = items.some(item => item.deviceType.toLowerCase().includes('watch'));
    const hasIPad = items.some(item => item.deviceType.toLowerCase().includes('ipad'));

    if (hasIPhone) return 0.15; // iPhones behouden waarde beter
    if (hasIPad) return 0.18; // iPads vallen ook goed mee
    if (hasAppleWatch) return 0.22; // Apple Watch daalt iets sneller
    if (hasSamsung) return 0.25; // Samsung daalt sneller
    return 0.20; // Default voor andere apparaten
  };

  const depreciationRate = getDepreciationRate();
  const futurePrice = Math.round(currentPrice * (1 - depreciationRate));
  const percentChange = ((futurePrice - currentPrice) / currentPrice * 100).toFixed(1);
  const potentialLoss = currentPrice - futurePrice;

  const deviceNameString = items.length === 1 ? formatProductName(items[0]) : '';

  // Splits a translated sentence on {device} so the device name stays in <strong>
  const renderDeviceSentence = (template: string, deviceName: string) => {
    const [before, after = ''] = template.split('{device}');
    return <>{before}<strong>{deviceName}</strong>{after}</>;
  };

  // Dynamische prijsschaal gebaseerd op werkelijke prijzen
  const priceMin = Math.max(50, Math.round(futurePrice * 0.7));
  const priceMax = Math.round(currentPrice * 1.1);
  const priceRange = priceMax - priceMin;
  const intervals = Array.from({ length: 5 }, (_, i) =>
    Math.round(priceMin + (priceRange * i / 4))
  );
  const priceScale = { min: priceMin, max: priceMax, intervals };

  // Convert price to Y coordinate with dramatic decline
  const priceToY = (price: number) => {
    const ratio = (price - priceScale.min) / (priceScale.max - priceScale.min);
    return 160 - (ratio * 120);
  };

  // Realistische data points gebaseerd op werkelijke prijzen
  const historicalPrice3m = Math.round(currentPrice * 1.05);
  const historicalPrice2m = Math.round(currentPrice * 1.03);
  const historicalPrice1m = Math.round(currentPrice * 1.01);
  const forecast3w = Math.round(currentPrice * (1 - depreciationRate * 0.3));

  const dataPoints = [
    { x: 60, y: priceToY(historicalPrice3m), price: historicalPrice3m, date: t('chartMonthsAgo').replace('{n}', '3'), type: 'historical' },
    { x: 120, y: priceToY(historicalPrice2m), price: historicalPrice2m, date: t('chartMonthsAgo').replace('{n}', '2'), type: 'historical' },
    { x: 180, y: priceToY(historicalPrice1m), price: historicalPrice1m, date: t('chartMonthsAgo').replace('{n}', '1'), type: 'historical' },
    { x: 240, y: priceToY(currentPrice), price: currentPrice, date: t('chartNow'), type: 'current' },
    { x: 300, y: priceToY(forecast3w), price: forecast3w, date: t('chartWeeksAhead').replace('{n}', '3'), type: 'forecast' },
    { x: 360, y: priceToY(futurePrice), price: futurePrice, date: t('chartMonthsAhead').replace('{n}', '2'), type: 'forecast' }
  ];

  const [animationProgress, setAnimationProgress] = React.useState(0);
  const [hoveredPoint, setHoveredPoint] = React.useState<typeof dataPoints[0] | null>(null);
  const [liveIndicatorPulse, setLiveIndicatorPulse] = React.useState(0);
  const [textVisible, setTextVisible] = React.useState(false);
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    // Dramatic line drawing animation (slower for impact)
    const timer = setTimeout(() => {
      const animate = () => {
        setAnimationProgress(prev => {
          if (prev < 1) {
            requestAnimationFrame(animate);
            return Math.min(prev + 0.012, 1); // Slower for dramatic effect
          }
          return 1;
        });
      };
      animate();
    }, 800);

    // Text fade-in for live feeling
    const textTimer = setTimeout(() => {
      setTextVisible(true);
    }, 2000);

    // Live indicator pulse
    const pulseInterval = setInterval(() => {
      setLiveIndicatorPulse(prev => (prev + 1) % 3);
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearTimeout(textTimer);
      clearInterval(pulseInterval);
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMousePosition({ x, y });

    // Find closest data point
    const svgX = (x / rect.width) * 360;
    const closest = dataPoints.find(point => Math.abs(point.x - svgX) < 25);
    setHoveredPoint(closest || null);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #fefefe 100%)',
      borderRadius: '20px',
      padding: '0',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      marginBottom: '20px',
      border: '1px solid #f1f3f5',
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "SF Pro Display", system-ui, sans-serif'
    }}>
      {/* Horizontal Layout: Chart Left, Analysis Right */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>

        {/* Left: Chart Section */}
        <div style={{
          width: '100%',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          padding: '28px 24px',
          position: 'relative'
        }}>
          {/* Professional Chart Header */}
          <div style={{
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '6px'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                margin: 0,
                color: '#1f2937',
                letterSpacing: '-0.01em'
              }}>
                {t('valueEvolution')}
              </h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                padding: '2px 6px',
                background: '#F3F4F6',
                borderRadius: '6px',
                border: '1px solid #E5E7EB',
                transition: 'all 0.8s ease'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#6B7280',
                  transition: 'background 0.6s ease'
                }}></div>
                <span style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#111827',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase'
                }}>
                  {t('liveBadge')}
                </span>
              </div>
            </div>
            <p style={{
              fontSize: '12px',
              color: '#6b7280',
              margin: 0,
              opacity: textVisible ? 1 : 0,
              transition: 'opacity 1s ease-in'
            }}>
              {t('realtimeAnalysisWarning')}
            </p>
          </div>

          {/* Dramatic Falling Chart */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid #e5e7eb',
            position: 'relative'
          }}>
            <svg
              width="100%"
              height="180"
              viewBox="0 0 380 180"
              style={{ display: 'block', cursor: 'crosshair' }}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => {
                setHoveredPoint(null);
              }}
            >
              <defs>
                <linearGradient id="dramaticFall" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: '#64748b', stopOpacity: 0.8 }} />
                  <stop offset="60%" style={{ stopColor: '#64748b', stopOpacity: 0.6 }} />
                  <stop offset="100%" style={{ stopColor: '#e11d48', stopOpacity: 0.7 }} />
                </linearGradient>
                <linearGradient id="forecastArea" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#94a3b8', stopOpacity: 0.1 }} />
                  <stop offset="100%" style={{ stopColor: '#94a3b8', stopOpacity: 0.03 }} />
                </linearGradient>
              </defs>

              {/* Y-axis with dramatic price range */}
              {priceScale.intervals.slice(1, 5).map((price, index) => {
                const y = priceToY(price);
                return (
                  <g key={price}>
                    <line x1="40" y1={y} x2="360" y2={y} stroke="#f1f5f9" strokeWidth="1" opacity="0.6" />
                    <text x="35" y={y + 3} textAnchor="end" fontSize="11" fontWeight="500" fill="#6b7280">
                      €{price}
                    </text>
                  </g>
                );
              })}

              {/* Professional forecast area */}
              <path
                d={`M ${dataPoints[3].x} ${dataPoints[3].y} C ${dataPoints[3].x + 20} ${dataPoints[3].y + 5}, ${dataPoints[4].x - 20} ${dataPoints[4].y - 30}, ${dataPoints[4].x} ${dataPoints[4].y} S ${dataPoints[5].x} ${dataPoints[5].y}, ${dataPoints[5].x} ${dataPoints[5].y}`}
                fill="url(#forecastArea)"
                opacity={animationProgress > 0.5 ? 1 : 0}
              />

              {/* Clean historical line */}
              <path
                d={`M ${dataPoints[0].x} ${dataPoints[0].y} Q ${dataPoints[1].x} ${dataPoints[1].y} ${dataPoints[2].x} ${dataPoints[2].y} Q ${dataPoints[2].x + 20} ${dataPoints[2].y - 5} ${dataPoints[3].x} ${dataPoints[3].y}`}
                stroke="#4B5563"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="200"
                strokeDashoffset={200 * (1 - Math.min(animationProgress * 1.5, 1))}
                style={{ transition: 'stroke-dashoffset 2.5s ease-out' }}
              />

              {/* Smooth forecast decline with Bezier curve */}
              <path
                d={`M ${dataPoints[3].x} ${dataPoints[3].y} C ${dataPoints[3].x + 20} ${dataPoints[3].y + 5}, ${dataPoints[4].x - 20} ${dataPoints[4].y - 30}, ${dataPoints[4].x} ${dataPoints[4].y} S ${dataPoints[5].x} ${dataPoints[5].y}, ${dataPoints[5].x} ${dataPoints[5].y}`}
                stroke="#94a3b8"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="4 4"
                strokeDashoffset={animationProgress < 0.5 ? 80 : 80 * (1 - ((animationProgress - 0.5) / 0.5))}
                style={{
                  transition: 'stroke-dashoffset 2.5s ease-in-out',
                  filter: 'drop-shadow(0 1px 2px rgba(148, 163, 184, 0.2))'
                }}
              />

              {/* Clean professional data points */}
              {dataPoints.map((point, index) => (
                <circle
                  key={index}
                  cx={point.x}
                  cy={point.y}
                  r={point.type === 'current' ? 4 : 3}
                  fill={point.type === 'forecast' ? '#9CA3AF' : '#4B5563'}
                  opacity={animationProgress > (index / dataPoints.length) ? 1 : 0}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    animation: point.type === 'current' ? 'currentPointPulse 2s ease-in-out infinite' : 'none'
                  }}
                  stroke='white'
                  strokeWidth={1}
                />
              ))}

              {/* Professional Timeline with equal spacing */}
              <text x="60" y="170" textAnchor="middle" fontSize="11" fill="#6B7280" fontWeight="400">{t('chartMonthsAgo').replace('{n}', '3')}</text>
              <text x="120" y="170" textAnchor="middle" fontSize="11" fill="#6B7280" fontWeight="400">{t('chartMonthsAgo').replace('{n}', '2')}</text>
              <text x="180" y="170" textAnchor="middle" fontSize="11" fill="#6B7280" fontWeight="400">{t('chartMonthsAgo').replace('{n}', '1')}</text>
              <text x="240" y="170" textAnchor="middle" fontSize="12" fill="#4B5563" fontWeight="600">{t('chartNow')}</text>
              <text x="300" y="170" textAnchor="middle" fontSize="11" fill="#6B7280" fontWeight="400">{t('chartWeeksAhead').replace('{n}', '3')}</text>
              <text x="360" y="170" textAnchor="middle" fontSize="11" fill="#6B7280" fontWeight="400">{t('chartMonthsAhead').replace('{n}', '2')}</text>

              {/* Clean "now" reference line */}
              <line x1="240" y1="20" x2="240" y2="150" stroke="#4B5563" strokeWidth="1" strokeDasharray="2 2" opacity="0.3" />
            </svg>

            {/* Professional AI analysis indicator */}
            <div style={{
              position: 'absolute',
              bottom: '12px',
              left: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              background: '#F3F4F6',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              opacity: textVisible ? 1 : 0,
              transition: 'opacity 1.2s ease-in'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                backgroundColor: '#6B7280',
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '8px',
                fontWeight: 'bold',
                color: 'white'
              }}>⚠</div>
              <span style={{ fontSize: '12px', color: '#111827', fontWeight: '500' }}>
                {t('valueDropsFasterThanExpected')}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Analysis Section */}
        <div style={{
          width: '100%',
          padding: '0 24px 28px 24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start'
        }}>
          {/* Urgent Analysis */}
          <div style={{
            marginBottom: '24px'
          }}>
            <h4 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#1f2937',
              margin: '0 0 12px 0',
              letterSpacing: '-0.01em'
            }}>
              {t('realtimeMarketAnalysis')}
            </h4>
            <p style={{
              fontSize: '14px',
              color: '#4b5563',
              lineHeight: '1.6',
              margin: '0 0 16px 0'
            }}>
              {items.length === 1
                ? renderDeviceSentence(t('deviceValueDropsSingle'), deviceNameString)
                : t('deviceValueDropsMultiple')}
              <strong style={{ color: '#1f2937' }}> {t('sellNowCouldEarn').replace('{amount}', String(potentialLoss))}</strong>
            </p>

            {/* Professional Analysis Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 16px',
              background: '#F3F4F6',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              {/* Professional AI Chip */}
              <div style={{
                width: '16px',
                height: '16px',
                backgroundColor: '#6B7280',
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '8px',
                fontWeight: '600',
                color: 'white'
              }}>AI</div>
              <span style={{
                fontSize: '13px',
                fontWeight: '500',
                color: '#111827',
                letterSpacing: '0.3px'
              }}>
                {t('expectedDecline')} {Math.abs(parseFloat(percentChange))}%
              </span>
            </div>
          </div>

          {/* Hover Interaction Area */}
          <div
            style={{
              padding: '16px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
              e.currentTarget.style.borderColor = '#f87171';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)';
              e.currentTarget.style.borderColor = '#e2e8f0';
            }}
          >
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#64748b',
              marginBottom: '4px'
            }}>
              {t('potentialLossWaiting')}:
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#dc2626'
            }}>
              €{potentialLoss}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced CSS animations */}
      <style>{`
        @keyframes urgentPulse {
          0%, 100% { 
            opacity: 1; 
            transform: scale(1);
          }
          50% { 
            opacity: 0.7; 
            transform: scale(1.2);
          }
        }
        
        @keyframes currentPointPulse {
          0%, 100% { 
            transform: scale(1); 
            opacity: 1; 
          }
          25% { 
            transform: scale(1.2); 
            opacity: 0.8; 
          }
          50% { 
            transform: scale(1.1); 
            opacity: 0.9; 
          }
          75% { 
            transform: scale(1.15); 
            opacity: 0.7; 
          }
        }
      `}</style>
    </div>
  );
};

const QuoteRequestSection = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    margin-top: 1.25rem;
  }
  
  @media (max-width: 480px) {
    margin-top: 1rem;
  }
`;

const QuoteRequestButton = styled.button`
  background: transparent;
  border: none;
  color: #8e8e93;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: not-allowed;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f7;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.45rem 0.85rem;
    border-radius: 8px;
    gap: 0.4rem;
  }
`;

const EWasteSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
`;

const CartPage: React.FC<CartPageProps> = ({ items, onRemoveItem, onBack, onSuccessNavigate }) => {
  // Use custom hook for all state management and handlers
  const {
    showCustomerServicePage,
    showSuccessPage,
    orderNumber,
    selectedCustomer,
    showDeleteModal,
    deviceToDelete,
    showContactModal,
    closingContactModal,
    contactForm,
    isSubmittingContact,
    showContactSuccess,
    isCreatingCalendarEvent,
    calendarEventStatus,
    calendarEventMessage,
    appointmentDetails,
    currentDeviceIndex,
    hasOpAanvraagItem,
    total,
    pageTopRef,
    language,
    quantities,
    t,
    handleQuantityChange,
    handleCompleteSale,
    handleCustomerServiceComplete,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleOpenContactModal,
    handleCloseContactModal,
    handleContactFormChange,
    handleContactFormSubmit,
    handleBackToHome,
    handleBackFromCustomerService,
    goToPrevDevice,
    goToNextDevice,
  } = useCartPage({ items, onRemoveItem, onSuccessNavigate });

  // Helper function to generate price impact factors based on device specs
  const getPriceImpactFactors = (device: CartItem) => {
    const factors: { icon: React.ReactNode, text: string }[] = [];

    // Check storage
    if (device.specs.storage) {
      const storage = device.specs.storage as string;
      if (parseInt(storage) < 256 || storage.includes("128") || storage.includes("64")) {
        factors.push({
          icon: <ChipIcon />,
          text: t('storageImpactLow').replace('{storage}', storage)
        });
      }
    }

    // Check condition
    if (device.specs.condition) {
      const condition = device.specs.condition as string;
      if (condition !== "A") {
        factors.push({
          icon: <CalendarIcon />,
          text: t('conditionImpact').replace('{condition}', getConditionLabel(condition, language))
        });
      }
    }

    // Check battery
    if (device.specs.battery) {
      const battery = device.specs.battery as string;
      if (battery !== "above95") {
        factors.push({
          icon: <BatteryIcon />,
          text: t('batteryImpactLow').replace('{battery}', getBatteryLabel(battery, language, true))
        });
      }
    }

    // If no specific factors, add general ones
    if (factors.length === 0) {
      factors.push({
        icon: <ChipIcon />,
        text: t('newerModelsImpact')
      });
      factors.push({
        icon: <CalendarIcon />,
        text: t('deviceAgeImpact')
      });
    }

    return factors;
  };

  if (showSuccessPage) {
    return (
      <SuccessPage
        orderNumber={orderNumber}
        email={selectedCustomer?.personalInfo?.email || ""}
        onBackToHome={handleBackToHome}
        deliveryMethod={appointmentDetails?.method as 'store' | 'home'}
        customerName={{
          firstName: selectedCustomer?.personalInfo?.firstName || "",
          lastName: selectedCustomer?.personalInfo?.lastName || ""
        }}
        appointment={{
          location: appointmentDetails?.location || "",
          date: appointmentDetails?.date || "",
          time: appointmentDetails?.time || "",
          address: appointmentDetails?.address || ""
        }}
        calendarEventStatus={calendarEventStatus}
        calendarEventMessage={calendarEventMessage}
        isCreatingCalendarEvent={isCreatingCalendarEvent}
      />
    );
  }



  return (
    <>
      <GlobalModalStyles />
      <Container>
        <div ref={pageTopRef} style={{ position: 'absolute', top: 0, height: 0 }} aria-hidden="true" />


        {!showCustomerServicePage ? (
          <>
            <CartHeader>
              <HeaderContent>
                <CartTitle>{t('cartTitle')}</CartTitle>
                <CartSubtitle>{items.length} {items.length === 1 ? t('selectedDevice') : t('selectedDevices')}</CartSubtitle>
              </HeaderContent>
              <BackButton onClick={onBack}>
                <BackArrowIcon /> {t('back')}
              </BackButton>
            </CartHeader>

            <CartGrid>
              <CartItems>
                {/* Batch selection banner removed */}

                {items.length === 0 ? (
                  <EmptyCartPremium onBack={onBack} />
                ) : (
                  <ModernCartContainer>
                    {items.length > 1 && (
                      <DeviceNavigation>
                        <NavButton onClick={goToPrevDevice} disabled={items.length <= 1}>
                          <LeftArrowIcon />
                        </NavButton>
                        <NavText>
                          {t('device')} {currentDeviceIndex + 1} {t('outOf')} {items.length}
                        </NavText>
                        <NavButton onClick={goToNextDevice} disabled={items.length <= 1}>
                          <RightArrowIcon />
                        </NavButton>
                      </DeviceNavigation>
                    )}
                    {items.length > 0 && items.map((item, index) => (
                      <React.Fragment key={item.id}>
                        {index === currentDeviceIndex && (
                          <CartItemCard
                            item={item}
                            quantity={quantities[item.id] || 1}
                            onQuantityChange={handleQuantityChange}
                            onDelete={handleDeleteClick}
                            language={language}
                            t={t}
                          />
                        )}
                      </React.Fragment>
                    ))}

                    {items.length > 0 && (
                      <CartActions
                        onCompleteSale={handleCompleteSale}
                        onBack={onBack}
                        itemsCount={items.length}
                        t={t}
                      />
                    )}
                  </ModernCartContainer>
                )}
              </CartItems>

              <CartSidebar
                onOpenContactModal={handleOpenContactModal}
                t={t}
              />
            </CartGrid>
          </>
        ) : (
          <CustomerServicePage
            cartItems={items}
            total={total}
            onBack={handleBackFromCustomerService}
            onComplete={handleCustomerServiceComplete}
            isSubmitting={isCreatingCalendarEvent}
            submitError={calendarEventStatus === 'error' ? calendarEventMessage : null}
          />
        )}


        {/* Contact Modal */}
        <CartContactModal
          isOpen={showContactModal}
          closing={closingContactModal}
          language={language}
          contactForm={contactForm}
          isSubmitting={isSubmittingContact}
          showSuccess={showContactSuccess}
          t={t}
          onClose={handleCloseContactModal}
          onFormChange={handleContactFormChange}
          onSubmit={handleContactFormSubmit}
        />

        {/* Delete Confirmation Modal */}
        <CartDeleteModal
          isOpen={showDeleteModal && !!deviceToDelete}
          language={language}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      </Container>
    </>
  );
};

export default CartPage; 